import { InternalRefetchQueriesInclude } from '@apollo/client';
import {
  REVIEWABLE_DOCUMENT_CATEGORIES,
  REVIEWABLE_DOCUMENT_TYPES,
  ReviewableDocumentCategoryKey,
  reviewableDocumentInputSchema,
} from '@bordrio/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  ReviewableDocumentWithTitleDescriptionFragment,
  useReuploadReviewableDocumentMutation,
} from '@src/generated/graphql';
import { useToast } from '@src/hooks/useToast';
import { logAndFormatError } from '@src/services/error-formatter';
import OrderNavigationButtons from '../../components/OrderNavigationButtons';
import ReviewableDocumentCategoryInput from '../../components/ReviewableDocumentCategoryInput/ReviewableDocumentCategoryInput';
import ReviewableDocumentFileInput from '../../components/ReviewableDocumentFileInput/ReviewableDocumentFileInput';
import { extractReviewableDocumentInputFromDocument } from '../../utils/reviewable-document';

const formSchema = z.object({
  reviewableDocument: reviewableDocumentInputSchema,
});

type FormData = z.infer<typeof formSchema>;

interface ReviewableDocumentReuploaderProps {
  reviewableDocument: ReviewableDocumentWithTitleDescriptionFragment;
  previousUrl: string | null;
  navigateToNextStep: () => void;
  title?: string;
  refetchQueries?: InternalRefetchQueriesInclude;
  disableCategorySelectionForCategories?: ReviewableDocumentCategoryKey[];
}

export function ReviewableDocumentReuploader({
  reviewableDocument,
  previousUrl,
  navigateToNextStep,
  title,
  refetchQueries,
  disableCategorySelectionForCategories,
}: ReviewableDocumentReuploaderProps): JSX.Element {
  const reviewableDocumentInput =
    extractReviewableDocumentInputFromDocument(reviewableDocument);

  const { handleSubmit, control, setValue } = useForm<FormData>({
    defaultValues: {
      reviewableDocument: reviewableDocumentInput,
    },
    resolver: zodResolver(formSchema),
  });
  const { i18n } = useTranslation();
  const [reuploadReviewableDocument] = useReuploadReviewableDocumentMutation({
    refetchQueries,
  });
  const toast = useToast();

  function onSubmit(formData: FormData): Promise<void> {
    return reuploadReviewableDocument({
      variables: {
        input: {
          reviewableDocumentId: reviewableDocument.id,
          input: formData.reviewableDocument,
        },
        locale: i18n.language ?? 'en',
      },
    })
      .then(() => {
        navigateToNextStep();
      })
      .catch((err) => toast.error(logAndFormatError(err)));
  }

  const documentCategory = Object.values(REVIEWABLE_DOCUMENT_CATEGORIES).find(
    (c) => c.id === reviewableDocument.categoryId,
  );

  const documentsWithCategory = Object.values(REVIEWABLE_DOCUMENT_TYPES).filter(
    (doc) => doc.categories.includes(reviewableDocument.categoryId),
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      <div className="space-y-2">
        <h2>{title ?? reviewableDocument.titleTranslated ?? 'Document'}</h2>
        {reviewableDocument.descriptionTranslated && (
          <p>{reviewableDocument.descriptionTranslated}</p>
        )}
      </div>
      {documentsWithCategory.length > 1 &&
      !reviewableDocument.documentTypeId &&
      documentCategory &&
      !disableCategorySelectionForCategories?.includes(
        documentCategory.key as ReviewableDocumentCategoryKey,
      ) ? (
        <ReviewableDocumentCategoryInput
          control={control}
          name="reviewableDocument"
          category={documentCategory.key as ReviewableDocumentCategoryKey}
          setValue={setValue}
        />
      ) : (
        <ReviewableDocumentFileInput
          horizontalLayout
          control={control}
          name="reviewableDocument"
        />
      )}

      <OrderNavigationButtons previousUrl={previousUrl} buttonType="submit" />
    </form>
  );
}

export default ReviewableDocumentReuploader;
